import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from "react-router-dom";
import { AuthContext, CsvTable } from 'components'
import {
  Box,
  Divider,
  Drawer,
  Stack,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Link } from "react-router-dom";
import { Scrollbar } from 'components/scrollbar';
import { StoreName, ExportCSV } from 'components/Settings';
import { items as menu_items } from './config';
import { SideNavItem } from './side-nav-item';
import { FavesIcon } from 'components/Icons';
import { AccountPopover } from './account-popover';
import { usePopover } from 'hooks/use-popover';
import { useLocalStorage } from 'hooks/use-local-storage'
import { SvgIcon } from '@mui/material';
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  SettingsEthernet as SettingsEthernetIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  Category as CategoryIcon,
  Collections as CollectionsIcon,
} from '@mui/icons-material';

export const SideNav = (props) => {
  const { open, onClose } = props;
  const [ seasonLimit, setSeasonLimit ] = useState(3);
  const [ settingsOpen, setSettingsOpen ] = useLocalStorage('sideNavSettingsCollapsible');
  const [ menuItems, setMenuItems ] = useState(menu_items);
  
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const pathname = useLocation().pathname;
  const { 
    account, 
    currentUser,
    items, 
    seasons, 
    selectedSeason, 
    setSelectedSeason, 
    selectedStore, 
    setSelectedStore, 
    collections, 
    selectedCollection, 
    setSelectedCollection,
    sortByDisplayOrder
  } = useContext(AuthContext);

  const accountPopover = usePopover();

  const handleSeasonChange = (e) => {
    setSelectedSeason(e.target.value)
  }
  
  const handleStoreChange = (e) => {
    setSelectedStore(e.target.value)
  }
  
  const handleCollectionChange = (e) => {
    setSelectedCollection(e.target.value)
  }

  useEffect(() => {
    if (Object.keys(seasons).sort((a, b) => seasons[b].displayOrder - seasons[a].displayOrder).slice(0, seasonLimit).indexOf(selectedSeason) === -1) {
      setSeasonLimit(Object.keys(seasons).length);
    }
  }, [])
  

  const content = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%'
        },
        '& .simplebar-scrollbar:before': {
          background: 'neutral.400'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh'
        }}
      >
        <Box sx={{ p: 3, pt: 0 }}>
          <Box
            component={Link}
            href="/"
            sx={{
              display: 'inline-flex',
              height: 32,
              width: 32
            }}
          >
          </Box>
          <FavesIcon sx={{ width: '100%', height: 40 }} />
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              borderRadius: 1,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
              p: '12px'
            }}
          >
            <div>
              <FormControl fullWidth variant="standard" sx={{ m: 1, minWidth: 120 }}>
                {currentUser.uid !== account.acctOwner ? (
                  <InputLabel 
                    htmlFor="selected-store"
                    focused={false}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      cursor: 'pointer'
                    }}
                  >
                    Store
                  </InputLabel>
                ) : (
                  <StoreName>
                    <InputLabel 
                      htmlFor="selected-store"
                      focused={false}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        cursor: 'pointer'
                      }}
                    >
                      Store
                      <EditIcon sx={{ position: 'relative', top: '-3px', ml: 1, height: '20px', width: '20px' }} />
                    </InputLabel>
                  </StoreName>
                )}
                <Select
                  inputProps={{ id: "selected-store" }}
                  sx={{
                    color: 'white'
                  }}
                  value={selectedStore}
                  onChange={handleStoreChange}
                  label="Store"
                >
                  {Object.keys(account.acctStores).map((s) => <MenuItem key={`store-${account.acctStores[s].displayName || 'MY STORE'}`} value={s}>{account.acctStores[s].displayName || 'MY STORE'}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={pathname==='/categories'} variant="standard" sx={{ m: 1, minWidth: 120 }}>
                <InputLabel htmlFor="selected-season" focused={false}>Season</InputLabel>
                <Select
                  inputProps={{ id: "selected-season" }}
                  sx={{
                    color: 'white'
                  }}
                  value={selectedSeason}
                  onChange={handleSeasonChange}
                  label="Season"
                >
                  {Object.keys(seasons).sort((a, b) => seasons[b].displayOrder - seasons[a].displayOrder).slice(0, seasonLimit).map((s) => <MenuItem key={`season-${seasons[s].displayName}`} value={s}>{seasons[s].displayName} - ({(items && items[s] && Object.keys(items[s]).length) || 0} items)</MenuItem>)}
                  {seasonLimit === 3 && <MenuItem onClickCapture={(e) => { setSeasonLimit(Object.keys(seasons).length); e.stopPropagation(); }} key={`season-show-more`} value={'show_more'}>Show more</MenuItem>}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={pathname==='/budgets' || pathname==='/categories'} variant="standard" sx={{ m: 1, minWidth: 120 }}>
                <InputLabel htmlFor="selected-collection" focused={false}>Collection</InputLabel>
                <Select
                  inputProps={{ id: "selected-collection" }}
                  sx={{
                    color: 'white'
                  }}
                  value={pathname==='/budgets' ? 'all' : selectedCollection}
                  onChange={handleCollectionChange}
                  label="Collection"
                >
                  {Object.keys(collections).map(e => ({ ...collections[e], id: e })).sort(sortByDisplayOrder).map((s) => <MenuItem key={`collection-${s.displayName}`} value={s.id}>{s.displayName}</MenuItem>)}
                </Select>
              </FormControl>
            </div>
          </Box>
        </Box>
        <Divider sx={{ borderColor: 'neutral.700' }} />
        <Box
          component="nav"
          sx={{
            flexGrow: 1,
            px: 2,
            py: 3
          }}
        >
          <Stack
            component="ul"
            spacing={0.5}
            sx={{
              listStyle: 'none',
              p: 0,
              m: 0
            }}
          >
            {menuItems.map((item, index) => {
              if (item === '-') {
                return <Divider key={`side-nar-separator-${index}`} sx={{ mt: '15px!important', mb: '15px!important', borderColor: 'neutral.700' }} />
              }

              const active = item.path ? (pathname === item.path) : false;

              return (
                <SideNavItem
                  active={active}
                  disabled={item.disabled}
                  external={item.external}
                  icon={item.icon}
                  key={`sidenavitem-${item.title}`}
                  path={item.path}
                  title={item.title}
                />
              );
            })}

            <CsvTable/>
            <ExportCSV/>

            <Divider sx={{ mt: '15px!important', mb: '15px!important', borderColor: 'neutral.700' }} />

            <ListItemButton onClick={() => { setSettingsOpen(!settingsOpen) }}>
              <ListItemIcon sx={{ minWidth: '40px' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText sx={{color: 'neutral.400',}} primary="Settings" />
              {settingsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse sx={{ pl: 2 }} in={settingsOpen} timeout="auto" unmountOnExit>
              <SideNavItem
                active={pathname === '/categories'}
                icon={<SvgIcon fontSize="small"><CategoryIcon /></SvgIcon>}
                path={'/categories'}
                title={'Categories'}
              />

              <SideNavItem
                active={pathname === '/collections'}
                icon={<SvgIcon fontSize="small"><CollectionsIcon /></SvgIcon>}
                path={'/collections'}
                title={'Collections'}
                />

              <SideNavItem
                active={pathname === '/users'}
                icon={<SvgIcon fontSize="small"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-1c0-2.66 5.33-4 8-4s8 1.34 8 4v1H4zm8-6c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"/></svg></SvgIcon>}
                path={'/users'}
                title={'Manage Users'}
              />

              {account?.features?.shopify && (
                <SideNavItem
                  active={pathname === '/settings'}
                  icon={<SvgIcon fontSize="small"><SettingsEthernetIcon /></SvgIcon>}
                  path={'/settings'}
                  title={'Integrations'}
                />
              )}
            </Collapse>

            <Divider sx={{ mt: '15px!important', mb: '15px!important', borderColor: 'neutral.700' }} />
            
            {/* <RestoreProducts/> */}
            <SideNavItem
              active={pathname === '/restore_products'}
              icon={<RestoreFromTrashIcon/>}
              path={'/restore_products'}
              title={'Recently Deleted'}
            />
          </Stack>
        </Box>
        <Avatar
            onClick={accountPopover.handleOpen}
            ref={accountPopover.anchorRef}
            sx={{
              cursor: 'pointer',
              height: 40,
              width: 40,
              margin: '20px'
            }}
            src="/assets/avatars/avatar-anika-visser.png"
          />
        
        <AccountPopover
          anchorEl={accountPopover.anchorRef.current}
          open={accountPopover.open}
          onClose={accountPopover.handleClose}
        />
      </Box>
    </Scrollbar>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: 'neutral.800',
            color: 'common.white',
            width: 280
          }
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: 'neutral.800',
          color: 'common.white',
          width: 280
        }
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

SideNav.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool
};